import React, { useEffect, useState } from 'react';
import { useStartOverModal } from '../context/StartOverModalContext';
import { useStepWorkspace } from '../context/StepWorkspaceContext';
import { BackupService } from '../services/backupService';
import styles from '../styles/stepWorkDiscoveryBanner.module.css';

const DISMISS_KEY = 'dismissed-step-work-archive-banner';

export type StepWorkDiscoveryMode = 'default' | 'workshop';

interface StepWorkDiscoveryBannerProps {
  user?: unknown;
  /** localStorage key suffix for per-page dismiss (optional) */
  dismissKeySuffix?: string;
  /**
   * workshop: only for members whose step work started well before workshop signup.
   * default: any meaningful current step work (legacy / non-workshop surfaces).
   */
  mode?: StepWorkDiscoveryMode;
  /** interest_at or confirmed_at — required when mode is workshop */
  workshopEngagedAt?: string | null;
}

const StepWorkDiscoveryBanner: React.FC<StepWorkDiscoveryBannerProps> = ({
  user,
  dismissKeySuffix = '',
  mode = 'default',
  workshopEngagedAt = null,
}) => {
  const { openStartOverModal } = useStartOverModal();
  const { revision: stepWorkspaceRevision } = useStepWorkspace();
  const storageKey = `${DISMISS_KEY}${dismissKeySuffix}`;
  const [visible, setVisible] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user) {
        setVisible(false);
        setChecking(false);
        return;
      }
      if (localStorage.getItem(storageKey) === '1') {
        setVisible(false);
        setChecking(false);
        return;
      }
      try {
        let shouldShow = false;
        if (mode === 'workshop') {
          shouldShow = await BackupService.hasPreWorkshopStepWork(workshopEngagedAt);
        } else {
          shouldShow = await BackupService.hasCurrentData('SAA', '12-steps');
        }
        if (!cancelled) {
          setVisible(shouldShow);
        }
      } catch {
        if (!cancelled) setVisible(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user, storageKey, stepWorkspaceRevision, mode, workshopEngagedAt]);

  if (checking || !visible) {
    return null;
  }

  const dismiss = () => {
    localStorage.setItem(storageKey, '1');
    setVisible(false);
  };

  return (
    <div className={styles.banner} role="status">
      <div className={styles.content}>
        <strong>Working the steps again?</strong>
        <span>
          {' '}
          — You have step work from before you joined this workshop. Use{' '}
          <strong>Save &amp; Start over</strong> in the menu to archive it and begin fresh for the
          workshop. We keep a copy for you.
        </span>
      </div>
      <div className={styles.actions}>
        <button type="button" className={styles.primary} onClick={openStartOverModal}>
          Save &amp; start over
        </button>
        <button type="button" className={styles.dismiss} onClick={dismiss} aria-label="Dismiss">
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default StepWorkDiscoveryBanner;
